// src/firebase/firebase.js

import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";
import { initializeAppCheck, ReCaptchaV3Provider, getToken } from "firebase/app-check";
// FCM removed - using Firestore notifications instead

// Validate required environment variables
const requiredEnvVars = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN', 
  'REACT_APP_FIREBASE_PROJECT_ID',
  'REACT_APP_FIREBASE_STORAGE_BUCKET',
  'REACT_APP_FIREBASE_APP_ID'
];

const missingVars = requiredEnvVars.filter(varName => !process.env[varName]);
if (missingVars.length > 0) {
  throw new Error(`Missing required Firebase environment variables: ${missingVars.join(', ')}`);
}

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  // FCM removed - messagingSenderId is not required for Auth/Firestore/Storage/Functions
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || undefined,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);

// Initialize App Check
const isProduction = process.env.NODE_ENV === 'production';
// Treat common local dev hosts as “preview” so debug App Check runs. Using 127.0.0.1 alone used to skip this
// and forced production reCAPTCHA → “App Check token is invalid” against enforced Storage.
const isPreview = typeof window !== 'undefined' && (
  window.location.hostname.includes('vercel.app') ||
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.endsWith('.localhost')
);

const hasRecaptchaKey = !!process.env.REACT_APP_RECAPTCHA_SITE_KEY;

// Env is always a string. "true"/"1"/empty must become boolean `true` so Firebase generates & logs a debug token.
// Only a non-empty value that is NOT the literal "true"/"1" is treated as a fixed token (already in Console).
const rawAppCheckDebug = (process.env.REACT_APP_APPCHECK_DEBUG_TOKEN || '').trim();
const appCheckDebugToken =
  !rawAppCheckDebug || /^true$/i.test(rawAppCheckDebug) || rawAppCheckDebug === '1'
    ? true
    : rawAppCheckDebug;
const usingFixedAppCheckDebugToken = typeof appCheckDebugToken === 'string';

// Production (non-preview): use real reCAPTCHA
const shouldEnableProductionAppCheck = isProduction && !isPreview && hasRecaptchaKey;

// Dev/preview: debug provider (register token in Console). Include !isProduction so LAN IPs during `npm start`
// still get App Check — not only localhost / 127.0.0.1 / Vercel hostnames.
const shouldEnableDevAppCheck = hasRecaptchaKey && (isPreview || !isProduction);

/** Set by initializeAppCheck — older firebase/app-check has no getAppCheck(); we keep the instance here. */
let firebaseAppCheckInstance = null;

if (shouldEnableProductionAppCheck) {
  try {
    firebaseAppCheckInstance = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(process.env.REACT_APP_RECAPTCHA_SITE_KEY),
      isTokenAutoRefreshEnabled: true
    });
    console.log('✅ Firebase App Check initialized (production)');
  } catch (error) {
    console.warn('⚠️ Firebase App Check initialization failed:', error);
  }
} else if (shouldEnableDevAppCheck) {
  try {
    // Debug mode: Firebase will print the debug token to console. Add it to Firebase Console → App Check → Apps → [web app] → Manage debug tokens
    if (typeof window !== 'undefined') {
      window.FIREBASE_APPCHECK_DEBUG_TOKEN = appCheckDebugToken;
    }
    firebaseAppCheckInstance = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(process.env.REACT_APP_RECAPTCHA_SITE_KEY),
      isTokenAutoRefreshEnabled: true
    });
    if (usingFixedAppCheckDebugToken) {
      console.log(
        '🔐 App Check (debug): Using fixed token from REACT_APP_APPCHECK_DEBUG_TOKEN — register this exact value in Firebase Console → App Check → your web app → Manage debug tokens:\n',
        appCheckDebugToken
      );
    } else {
      console.log(
        '🔐 App Check (debug): Look below for Firebase’s "App Check debug token" line — copy it into Firebase Console → App Check → your web app → Manage debug tokens. (Env REACT_APP_APPCHECK_DEBUG_TOKEN=true or unset enables this.)'
      );
    }
    // Always warm the provider so the SDK prints/logs the debug token when using boolean true
    getToken(firebaseAppCheckInstance, true).catch(() => {});
  } catch (error) {
    console.warn('⚠️ Firebase App Check (debug) initialization failed:', error);
  }
} else {
  if (isPreview && !hasRecaptchaKey) {
    console.warn('⚠️ App Check disabled for dev: REACT_APP_RECAPTCHA_SITE_KEY not set. Sign-in may be rejected if Firebase enforces App Check.');
  } else if (!isPreview && !hasRecaptchaKey) {
    console.warn('⚠️ App Check not initialized - REACT_APP_RECAPTCHA_SITE_KEY not set');
  }
}

/**
 * Clears all Firebase-related browser storage (localStorage keys + known IndexedDB databases).
 * Call this when App Check enters its 24-hour throttle state so the SDK starts fresh on reload.
 */
export function clearFirebaseStorageState() {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('firebase') || key.includes('appCheck'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (_) {}

  ['firebaseLocalStorageDb', 'firebase-heartbeat-database', 'firebase-installations-database'].forEach(name => {
    try { indexedDB.deleteDatabase(name); } catch (_) {}
  });
}

/**
 * Returns true if an error is an App Check throttle/token failure that can be
 * self-healed by clearing browser storage and reloading.
 */
export function isAppCheckThrottleError(err) {
  if (!err) return false;
  const code = err.code || '';
  const msg = (err.message || '').toLowerCase();
  return (
    code.startsWith('appCheck/') ||
    code === 'auth/app-check-token-error' ||
    msg.includes('app check') ||
    msg.includes('appcheck') ||
    msg.includes('app-check')
  );
}

/**
 * Wait for a valid App Check token before Storage uploads (localhost / enforced Storage).
 * Returns whether it is safe to proceed: if App Check is initialized and getToken fails,
 * returns { ok: false } — otherwise uploads can get 403 even when Auth and Storage rules pass.
 */
export async function ensureAppCheckToken(forceRefresh = false) {
  if (!firebaseAppCheckInstance) {
    return { ok: true, skipped: true };
  }
  try {
    await getToken(firebaseAppCheckInstance, forceRefresh);
    return { ok: true };
  } catch (e) {
    const msg = e?.message || String(e);
    console.warn('ensureAppCheckToken:', msg);
    return {
      ok: false,
      errorMessage:
        'App Check could not issue a token (often fixed by registering the debug token from the ' +
        'browser console under Firebase Console → App Check → your web app → Manage debug tokens). ' +
        `Details: ${msg}`,
    };
  }
}

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Initialize functions with explicit region
export const functions = getFunctions(app, 'europe-west2');

// Debug: Log Firebase config for verification (without sensitive data)
console.log('🔧 Firebase Config:', {
  projectId: firebaseConfig.projectId,
  authDomain: firebaseConfig.authDomain,
  hasApiKey: !!firebaseConfig.apiKey,
  hasMessagingSenderId: !!firebaseConfig.messagingSenderId,
  hasAppId: !!firebaseConfig.appId
});

// FCM removed - using Firestore notifications instead
// This file now only handles Firebase core services (Auth, Firestore, Storage, Functions)
